import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import ReactDOM from 'react-dom';
import CloseIcon from './images/close.svg';
import { ModalPrimitive } from '#app/components/ModalForm/components/ModalPrimitive';
import {
  ModalHeader,
  ModalClose,
  ModalCloseLanding,
  ModalTitle,
} from '#app/components/ModalForm/components/styles';
import { useDraggable } from '#app/components/ModalForm/hooks/useDraggable';
import { useModalKeyboard } from '#app/components/ModalForm/hooks/useModalKeyboard';

export type CloseButtonVariant = 'default' | 'landing';

export type ModalSize =
  | 'extra-small'
  | 'very-small'
  | 'small'
  | 'medium'
  | 'large'
  | 'extra-large'
  | 'avatar';

export interface ModalDragRenderProps {
  handleMouseDown: (e: React.MouseEvent) => void;
  isDragging: boolean;
}

export interface ComposableModalProps {
  show: boolean;
  trigger?: React.ReactNode;
  name?: string;
  size?: ModalSize;
  /** Заголовок — рендерится внутри модалки с перетаскиванием за шапку */
  title?: string;
  /** Стили для текста заголовка (h2) */
  titleStyle?: React.CSSProperties;
  /** Стили для всей шапки (контейнер заголовка + кнопка закрытия) */
  headerStyle?: React.CSSProperties;
  /** Стили для кнопки закрытия */
  closeButtonStyle?: React.CSSProperties;
  /** Вариант кнопки закрытия: default — стили ModalClose, landing — стили ModalCloseLanding (серая кнопка 32px) */
  closeButtonVariant?: CloseButtonVariant;
  /** Своя иконка для кнопки закрытия (иначе используется дефолтная из ./images/close.svg) */
  closeIcon?: React.ReactNode;
  /** Обработчик закрытия — кнопка закрытия в шапке, также вызывается по Escape */
  onClose?: () => void;
  /** Опционально: по Enter вызывается этот колбэк (например, отправка формы) */
  onSubmit?: () => void;
  children: React.ReactNode;
  style?: React.CSSProperties;
  id?: string;
  disableAnimation?: boolean;
}

type ModalRegistryEntry = {
  show: (callback?: () => void) => void;
  hide: () => void;
};

const modalRegistry: Record<string, ModalRegistryEntry> = {};

function handleShow() {
  const modalElement = document.getElementById('modal');
  if (modalElement) {
    modalElement.classList.add('visible');
  }
}

function handleClose() {
  const modalElement = document.getElementById('modal');
  if (modalElement) {
    modalElement.classList.remove('visible');
  }
}

export function showByName(name: string) {
  const entry = modalRegistry[name];
  if (entry) {
    entry.show(() => handleShow());
  }
}

export function hideByName(name: string) {
  handleClose();
  const entry = modalRegistry[name];
  if (entry) {
    entry.hide();
  }
}

type ModalDragContextValue = {
  handleMouseDown: (e: React.MouseEvent) => void;
  isDragging: boolean;
};

const ModalDragContext = createContext<ModalDragContextValue | null>(null);

export function useModalDrag(): ModalDragContextValue | null {
  return useContext(ModalDragContext);
}

function ComposableModal({
  show: showProp,
  trigger,
  name,
  size,
  title,
  titleStyle,
  headerStyle,
  closeButtonStyle,
  closeButtonVariant = 'default',
  closeIcon,
  onClose,
  onSubmit,
  children,
  style,
  id,
  disableAnimation = false,
}: ComposableModalProps) {
  const [show, setShow] = useState(showProp);
  const prevShowPropRef = useRef(showProp);
  const { dragRef, isDragging, handleMouseDown } = useDraggable();

  useModalKeyboard({
    onClose: onClose ?? undefined,
    onSubmit: onSubmit ?? undefined,
    enabled: !!showProp,
  });

  const showRef = useCallback((callback?: () => void) => {
    setShow(true);
    callback?.();
  }, []);

  const hideRef = useCallback(() => {
    setShow(false);
    handleClose();
  }, []);

  useEffect(() => {
    if (showProp !== prevShowPropRef.current) {
      prevShowPropRef.current = showProp;
      setShow(!!showProp);
    }
  }, [showProp]);

  useEffect(() => {
    if (name) {
      modalRegistry[name] = {
        show: showRef,
        hide: hideRef,
      };
      return () => {
        delete modalRegistry[name];
      };
    }
  }, [name, showRef, hideRef]);

  if (!show) {
    return <>{trigger ?? null}</>;
  }

  const modalElement = document.getElementById('modal');
  if (!modalElement) {
    return <>{trigger ?? null}</>;
  }

  handleShow();

  const dragValue: ModalDragContextValue = { handleMouseDown, isDragging };
  const hasBuiltInHeader = title != null || onClose != null;

  const closeIconContent = closeIcon ?? <img src={CloseIcon} alt="Close" />;
  const CloseButtonWrapper = closeButtonVariant === 'landing' ? ModalCloseLanding : ModalClose;

  const content = (
    <ModalDragContext.Provider value={dragValue}>
      <ModalPrimitive
        ref={dragRef}
        id={id}
        data-should-animate={!disableAnimation && showProp ? 'true' : 'false'}
        style={style}
        {...(size !== undefined
          ? ({ size } as React.ComponentProps<typeof ModalPrimitive> & { size: ModalSize })
          : {})}
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        {hasBuiltInHeader && (
          <ModalHeader data-dragging={isDragging} onMouseDown={handleMouseDown} style={headerStyle}>
            {title != null && title !== '' && <ModalTitle style={titleStyle}>{title}</ModalTitle>}
            {onClose != null && (
              <CloseButtonWrapper
                onClick={onClose}
                onMouseDown={(e) => e.stopPropagation()}
                style={closeButtonStyle}
              >
                {closeIconContent}
              </CloseButtonWrapper>
            )}
          </ModalHeader>
        )}
        {children}
      </ModalPrimitive>
    </ModalDragContext.Provider>
  );

  return (
    <>
      {trigger}
      {ReactDOM.createPortal(content, modalElement)}
    </>
  );
}

ComposableModal.show = showByName;
ComposableModal.hide = hideByName;

export { ComposableModal };
